
import { API_URL, API_BASE_URL } from "@/config/api";

export const getAuthHeader = (): Record<string, string> => {
  const token = localStorage.getItem("authToken");
  // Use 'Token' prefix instead of 'Bearer' for authentication
  return token ? { Authorization: `Token ${token}` } : {};
};

export const createApiRequest = (
  endpoint: string, 
  method: string = "GET", 
  body: any = null,
  additionalHeaders: Record<string, string> = {}
): Request => {
  const token = localStorage.getItem("authToken");
  
  if (!token) {
    console.error("No authentication token found when creating API request");
  } else {
    console.log(`Creating API request with token: ${token.substring(0, 5)}...`);
  }
  
  const headers = {
    "Content-Type": "application/json",
    ...getAuthHeader(),
    ...additionalHeaders,
  };

  const requestOptions: RequestInit = {
    method,
    headers,
  };

  if (body && method !== "GET") {
    requestOptions.body = JSON.stringify(body);
  }

  const url = `${API_URL}${endpoint}`;
  console.log(`Creating API request to: ${url}`);
  
  return new Request(url, requestOptions);
};

export const claimTrip = async (tripId: string): Promise<any> => {
  const token = localStorage.getItem("authToken");
  if (!token) {
    throw new Error("No authentication token found");
  }
  
  // Get organization name from local storage if available
  const userData = localStorage.getItem("userData");
  let organization = {};
  
  if (userData) {
    try {
      const parsedData = JSON.parse(userData);
      if (parsedData.organization?.name) {
        organization = { organization_name: parsedData.organization.name };
      }
    } catch (error) {
      console.error("Error parsing user data:", error);
    }
  }
  
  const response = await fetch(`${API_URL}trips/${tripId}/claim/`, {
    method: "POST",
    headers: {
      "Authorization": `Token ${token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify(organization)
  });
  
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || "Failed to claim trip");
  }
  
  return response.json();
};

export const fetchScheduledTrips = async (startDate: string, endDate: string): Promise<any> => {
  const token = localStorage.getItem("authToken");
  if (!token) {
    throw new Error("No authentication token found");
  }
  
  const response = await fetch(
    `${API_URL}trips/?type=claimed&pickup_at__gte=${startDate}&pickup_at__lte=${endDate}`,
    {
      method: "GET",
      headers: {
        "Authorization": `Token ${token}`,
        "Content-Type": "application/json"
      }
    }
  );
  
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || "Failed to fetch scheduled trips");
  }
  
  return response.json();
};

export const fetchPayouts = async (startDate: string, endDate: string): Promise<any> => {
  const token = localStorage.getItem("authToken");
  if (!token) {
    throw new Error("No authentication token found");
  }
  
  const response = await fetch(
    `${API_URL}trips/payouts/?start_date=${startDate}&end_date=${endDate}`,
    {
      method: "GET",
      headers: {
        "Authorization": `Token ${token}`,
        "Content-Type": "application/json"
      }
    }
  );
  
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || "Failed to fetch payout data");
  }
  
  return response.json();
};

export const fetchUserProfile = async (): Promise<any> => {
  const token = localStorage.getItem("authToken");
  if (!token) {
    throw new Error("No authentication token found");
  }
  
  console.log("Fetching user profile with token:", token);
  
  const response = await fetch(
    `${API_URL}disco-auth/users/me/`,
    {
      method: "GET",
      headers: {
        "Authorization": `Token ${token}`, // Use Token prefix instead of Bearer
        "Content-Type": "application/json"
      }
    }
  );
  
  if (!response.ok) {
    const errorData = await response.json();
    console.error("Failed to fetch user profile:", errorData);
    throw new Error(errorData.detail || "Failed to fetch user profile data");
  }
  
  const userData = await response.json();
  console.log("User profile data received:", userData);
  
  // Save user data to localStorage for other components to use
  localStorage.setItem("userData", JSON.stringify(userData));
  
  return userData;
};
